import { memo, useCallback, useMemo, MouseEvent } from 'react';

import { Typography, Paper, Button, Icon, Collapse } from '@material-ui/core';
import clsx from 'clsx';
import { v4 as uuid } from 'uuid';

import { LightTooltip } from 'components';
import { getCellRenderer } from 'components/DataTable/cellrenders';
import { DefineColumn, SelectedRows } from 'components/DataTable/types';
import { useDataTestId } from 'hooks';

import { useStyles } from './styles';

type RowType<T> = T & {
  id: string;
};

interface RowProps<T, C> {
  row: RowType<T>;
  rows: RowType<T>[];
  index: number;
  defineColumns: DefineColumn<any, T>[];
  selectable?: boolean;
  onRowClick?(row: RowType<T>): void;
  expansible?: boolean;
  expanded: Record<string, boolean>;
  context?: C;
  onExpand?(rowId: string): void;
  selectedRows?: SelectedRows<T>;
  allExpanded?: boolean;
}

const expansibleWidth = 70;
const defaultCollapseSize = 44;
const similarSchoolsCollapseSize = 64;

function RowWithoutMemo<T = any, C = any>({
  row,
  rows,
  index,
  defineColumns,
  selectable,
  onRowClick,
  expansible,
  onExpand,
  expanded,
  context,
  selectedRows,
  allExpanded,
}: RowProps<T, C>): JSX.Element {
  const classes = useStyles();
  const { getTestId } = useDataTestId();

  const memoIsExpanded = useMemo(() => {
    return allExpanded || !!expanded[row.id];
  }, [row, expanded, allExpanded]);

  const memoRowIsSelected = useMemo(() => {
    if (!selectedRows) {
      return false;
    }

    return !!selectedRows[String(row.id)];
  }, [row.id, selectedRows]);

  const memoGridTemplateColumns = useMemo(() => {
    const templateColumns: string[] = defineColumns.map(column => {
      const { minWidth, maxWidth } = column;
      if (minWidth !== undefined && maxWidth !== undefined) {
        return `minmax(${minWidth}px, ${maxWidth}px)`;
      }

      if (minWidth !== undefined && maxWidth === undefined) {
        return `minmax(${minWidth}px, 1fr)`;
      }

      if (minWidth === undefined && maxWidth !== undefined) {
        return `minmax(220px, ${maxWidth}px)`;
      }

      return 'minmax(220px, 1fr)';
    });

    return templateColumns;
  }, [defineColumns]);

  const memoCollapseSize = useMemo(() => {
    return (context as any)?.similarSchoolsCompare
      ? similarSchoolsCollapseSize
      : defaultCollapseSize;
  }, [context]);

  const handleRowClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.preventDefault();

      if (onRowClick && selectable) {
        event.stopPropagation();
        onRowClick(row);
      }
    },
    [onRowClick, row, selectable],
  );

  const cellRender = useCallback(
    (key: string, rowValue: any) => {
      const column = defineColumns.find(
        defineColumn => defineColumn.id === key,
      );

      const isSimilarSchools = (context as any)?.similarSchoolsCompare;

      const props = column?.props ?? {};

      if (column && column.cellRenderer) {
        const { cellRenderer: cellRenderComponent } = column;
        const Component = getCellRenderer(cellRenderComponent);

        if (Component) {
          return (
            <Component
              row={row}
              rows={rows}
              data={rowValue}
              index={index}
              columnId={column.id}
              context={context}
              columnsQuantity={defineColumns.length}
              expanded={expanded}
              expansible={expansible}
              {...column}
            />
          );
        }

        return (
          <div className={classes.defaultRenderWrapper}>
            <Typography
              noWrap
              component="div"
              className={clsx(classes.defaultRender, {
                'similar-schools': isSimilarSchools,
              })}
              variant="subtitle2"
              color="textSecondary"
              {...props}
            >
              {rowValue}
            </Typography>
          </div>
        );
      }

      if (column?.hasTooltip) {
        return (
          <LightTooltip title={rowValue} placement="top-start">
            <div className={classes.defaultRenderWrapper}>
              <Typography
                noWrap
                component="div"
                className={clsx(classes.defaultRender, {
                  'similar-schools': isSimilarSchools,
                })}
                variant="subtitle2"
                color="textSecondary"
                {...props}
              >
                {rowValue}
              </Typography>
              {isSimilarSchools && (
                <Typography
                  noWrap
                  component="div"
                  className={clsx(
                    classes.defaultRender,
                    classes.similarSchoolsTitle,
                  )}
                  variant="subtitle2"
                  {...props}
                >
                  Escolas similares
                </Typography>
              )}
            </div>
          </LightTooltip>
        );
      }

      return (
        <div className={classes.defaultRenderWrapper}>
          <Typography
            noWrap
            component="div"
            className={clsx(classes.defaultRender, {
              'similar-schools': isSimilarSchools,
            })}
            variant="subtitle2"
            color="textSecondary"
            {...props}
          >
            {rowValue}
          </Typography>
        </div>
      );
    },
    [
      classes.defaultRender,
      classes.defaultRenderWrapper,
      classes.similarSchoolsTitle,
      context,
      defineColumns,
      expanded,
      expansible,
      index,
      row,
      rows,
    ],
  );

  return (
    <Paper
      elevation={2}
      className={clsx(classes.root, {
        'similar-schools': (context as any)?.similarSchoolsCompare,
        selected: memoRowIsSelected,
        selectable,
      })}
      {...getTestId(`row-${row.id}`)}
    >
      <Collapse
        in={memoIsExpanded}
        collapsedSize={memoCollapseSize}
        className={classes.collapse}
      >
        <div
          className={classes.grid}
          role="presentation"
          onClick={handleRowClick}
          style={{
            gridTemplateColumns: `${memoGridTemplateColumns.join(' ')} ${
              expansible ? `${expansibleWidth}px` : ''
            }`,
          }}
        >
          {defineColumns.map((column, index) => (
            <div
              className={clsx(classes.cellWrapper, {
                [classes.separator]: index > 1,
                'render-component': column.cellRenderer,
                'similar-schools': (context as any)?.similarSchoolsCompare,
              })}
              key={`cell-${column.id}-${uuid()}`}
            >
              {cellRender(column.id, row[column.id as keyof RowType<T>])}
            </div>
          ))}
          {expansible && onExpand && !allExpanded && (
            <div
              className={classes.expansibleContainer}
              style={{ width: expansibleWidth }}
            >
              {rows.length > 1 && (
                <LightTooltip
                  title={memoIsExpanded ? 'Fechar' : 'Expandir'}
                  placement="top"
                >
                  <Button
                    onClick={() => onExpand(row.id)}
                    className={classes.expandButton}
                  >
                    <Icon
                      color={memoIsExpanded ? 'primary' : undefined}
                      className={clsx(classes.expandIcon, {
                        expanded: memoIsExpanded,
                      })}
                    >
                      expand_more
                    </Icon>
                  </Button>
                </LightTooltip>
              )}
            </div>
          )}
        </div>
      </Collapse>
    </Paper>
  );
}

export const Row = memo(RowWithoutMemo) as typeof RowWithoutMemo;
